/**
 * Anexa ao evento a identidade do valor que foi realmente lançado, antes de ele
 * sair para o Sentry.
 *
 * Por que existe: erros com `mechanism: onerror` — lançados fora da zone do
 * Angular e capturados pelo `globalHandlers` do SDK — não passam pelo
 * GlobalErrorHandler nem pelo SentryErrorInterceptor, que são os dois pontos
 * onde o app hoje enriquece o contexto. Para esses eventos o `beforeSend` é o
 * ÚNICO lugar do código da aplicação por onde eles passam.
 *
 * O caso concreto que motivou isto (TREINO-E, `InvalidStateError` em
 * /training/:id, só iOS) chega ao Sentry sem nada acionável: o frame é
 * sintetizado a partir de `(source, lineno, colno)` do `onerror`, então
 * `stack.function` é nulo e `stack.filename` é o URL do documento — não um
 * `.js`. Sobra a mensagem genérica da DOMException, que é igual para dezenas de
 * causas diferentes.
 *
 * O que passamos a gravar:
 *  - tags `error.name` e `error.code` — a DOMException carrega a identidade real
 *    aí (`InvalidStateError` / 11), e tag é filtrável na UI do Sentry
 *    (`error.name:InvalidStateError`), o que separa esta issue de outros
 *    `InvalidStateError` que apareçam depois;
 *  - extra `error.raw_stack` — o `.stack` cru do objeto lançado, quando existe.
 *    É a única coisa que pode nomear o ficheiro de origem, já que o frame
 *    sintetizado pelo SDK não nomeia;
 *  - extra `error.raw_kind` — gravado SEMPRE, mesmo quando não há nada mais a
 *    extrair. Sem ele, um evento sem tags seria ambíguo entre "a instrumentação
 *    não subiu nesta release" e "o WebKit não entregou o objeto do erro".
 *
 * Vale para qualquer erro, não só DOMException: o custo é o mesmo e o contexto
 * ajuda igual. Nada aqui altera o comportamento do app — só o payload do evento.
 */

/**
 * Visão estrutural mínima do evento, com apenas os campos que escrevemos. Segue
 * o mesmo motivo do sentry-event-throttle.ts para não importar o tipo real:
 * @sentry/core não é dependência declarada do app e o tipo do evento não é
 * reexportado pelo @sentry/angular.
 *
 * `tags` é `unknown` (e não `string`) de propósito: no tipo do SDK os valores
 * são `Primitive`, que não é atribuível a `string` — declarar mais estreito aqui
 * faria o `extends` falhar na composição do beforeSend.
 */
interface ISentryEventContextShape {
  tags?: Record<string, unknown>;
  extra?: Record<string, unknown>;
}

/** Visão estrutural mínima do hint; só o valor original interessa. */
interface ISentryHintShape {
  originalException?: unknown;
}

/**
 * Descreve o valor lançado em uma palavra, para o extra `error.raw_kind`:
 * o nome do construtor quando existe (`DOMException`, `TypeError`, `Event`) e o
 * `typeof` como último recurso (`string` lançada, por exemplo).
 */
function describeThrownValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';

  const constructorName = (value as { constructor?: { name?: unknown } }).constructor?.name;
  return typeof constructorName === 'string' && constructorName
    ? constructorName
    : typeof value;
}

/**
 * Devolve uma cópia do evento com a identidade do erro anexada. Genérica para o
 * tipo do próprio SDK atravessar sem conversão na chamada.
 *
 * Nunca lança: um throw dentro do `beforeSend` faz o SDK descartar o evento, o
 * que trocaria "evento com pouco contexto" por "nenhum evento" — estritamente
 * pior que o estado atual. Ler `.name`/`.code`/`.stack` de um valor arbitrário
 * pode cair num getter hostil, então o catch é funcional, não decorativo.
 */
export function attachErrorIdentity<T extends ISentryEventContextShape>(
  event: T,
  hint?: ISentryHintShape,
): T {
  try {
    const thrown = hint?.originalException;

    const tags: Record<string, unknown> = { ...event.tags };
    const extra: Record<string, unknown> = { ...event.extra };

    extra['error.raw_kind'] = describeThrownValue(thrown);

    const name = (thrown as { name?: unknown })?.name;
    if (typeof name === 'string' && name) {
      tags['error.name'] = name;
    }

    // `code` é o campo legado da DOMException (InvalidStateError == 11).
    // Convertido para string porque valor de tag no Sentry é sempre string.
    const code = (thrown as { code?: unknown })?.code;
    if (typeof code === 'number') {
      tags['error.code'] = String(code);
    }

    const stack = (thrown as { stack?: unknown })?.stack;
    if (typeof stack === 'string' && stack) {
      extra['error.raw_stack'] = stack;
    }

    // O cast é necessário porque o spread produz `T & { tags; extra }`, que o TS
    // não reconhece como `T` — embora as duas chaves substituídas já existam em `T`.
    return { ...event, tags, extra } as T;
  } catch {
    // Enriquecer é best-effort: na dúvida, envia o evento como estava.
    return event;
  }
}
